import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Fade,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { brandColors } from 'types/colors';
import { memo, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AlertDialogSlide from '../../components/Dialog/AlertDialogSlide';
import CustomSnackbar from '../../components/CustomSnackbar';
import DropdownMenu from '../../components/DropdownMenu';
import GenericModal from 'components/GenericModal';
import Loading from 'components/Loading/Loading';
import MDBox from '../../components/MDBox';
import MDInput from '../../components/MDInput';
import MDTypography from '../../components/MDTypography';
import DashboardNavbar from '../../examples/Navbars/DashboardNavbar';
import DashboardLayout from '../LayoutContainers/DashboardLayout';
import DeviceLocation from './DeviceLocation';
import DeviceConfigurationSections from './DeviceConfigurationSections';
import DevicesSelect from './DevicesSelect';
import {
  deleteDeviceValues,
  fetchUserDevices,
  updateDeviceValues,
  setDevicesList,
  setSelectedBuilding,
  updateFilteredDevicesWithSync,
  setSearchString,
} from '../../stores/sensor/sensorStore';
import { fetchDeviceInfo } from '../../stores/sensor/controllersSlice';
import { getBuildings } from '../../stores/sensor/sensorStore';
import { getHvacEquipment } from '../../services/hvacEquipmentService';
// Helper functions for filtering
import { generateManagementFilterOptions } from '../../helpers/filterHelpers';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import HubIcon from '@mui/icons-material/Hub';

import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { height } from '@mui/system';

const SENSOR_MODEL_OPTIONS = [
  { name: 'SEN54', value: 'SEN54' },
  { name: 'SPS30', value: 'SPS30' },
  { name: 'SEN66', value: 'SEN66' },
];
const CONTROLLER_MODEL_OPTIONS = [
  {
    name: 'CTU4501-CP-HP-SUPER-ST-190714',
    value: 'CTU4501-CP-HP-SUPER-ST-190714',
  },
  {
    name: 'CTU4501-CP-HP-SUPER-EC-UV-242013',
    value: 'CTU4501-CP-HP-SUPER-EC-UV-242013',
  },
  {
    name: 'HY08AC3-4-Thermostat',
    value: 'HY08AC3-4-Thermostat',
  },
  { name: 'RELAY_2CH', value: 'RELAY_2CH' },
  { name: 'RELAY_2CH_NC', value: 'RELAY_2CH_NC' },
  { name: 'RELAY_4CH', value: 'RELAY_4CH' },
  { name: 'RELAY_4CH_NC', value: 'RELAY_4CH_NC' },
  { name: 'ALFA-COM-1HMB', value: 'ALFA-COM-1HMB' },
  { name: 'CTU4501-P2-H3-SUPER-3-17177701', value: 'CTU4501-P2-H3-SUPER-3-17177701' },
  { name: 'HY01AC', value: 'HY01AC' },
];

// A controller that owns HVAC equipment must stay in that equipment's building.
const ControllerHvacMoveDialog = ({ open, onClose }) => (
  <AlertDialogSlide
    open={open}
    title="Controller connected to HVAC equipment"
    content="This controller is assigned to HVAC equipment and cannot be moved to another building. Reassign or delete that HVAC equipment first."
    handleClose={onClose}
  />
);

const UNIT_TYPE_OPTIONS = [
  { name: 'AHU', value: 'AHU' },
  { name: 'FCU', value: 'FCU' },
];
const COOLING_TYPE_OPTIONS = [
  { name: 'chiller (water)', value: 'chiller' },
  { name: 'gas (DX/VRF)', value: 'gas' },
];
const HEATING_TYPE_OPTIONS = {
  AHU: [
    { name: 'heating coil', value: 'heating coil' },
    { name: 'hot water', value: 'hot water' },
  ],
  FCU: [
    { name: 'heating coil', value: 'heating coil' },
    { name: 'hot water', value: 'hot water' },
  ],
};
const SPACE_OPTIONS = [
  { name: 'office room', value: 'office room' },
  { name: 'conference room', value: 'conference room' },
  { name: 'open space', value: 'open space' },
  { name: 'Open Corridor AHU', value: 'open corridor ahu' },
  { name: 'hotel room', value: 'hotel room' },
  { name: 'Eating space', value: 'Eating space' },
  { name: 'Auditorium', value: 'auditorium' },
];
const FCU_AREA_CFM_ROWS = [
  { area_m2: 12, area_cfm: 350 },
  { area_m2: 25, area_cfm: 600 },
  { area_m2: 50, area_cfm: 1000 },
  { area_m2: 75, area_cfm: 1500 },
  { area_m2: 100, area_cfm: 2000 },
  { area_m2: 150, area_cfm: 3000 },
  { area_m2: 200, area_cfm: 4000 },
  { area_m2: 250, area_cfm: 5000 },
];
const AHU_AREA_FAN_KW_ROWS = [
  { area_m2: 100, area_fan_kw: 1.0 },
  { area_m2: 150, area_fan_kw: 1.5 },
  { area_m2: 250, area_fan_kw: 2.5 },
  { area_m2: 300, area_fan_kw: 3.0 },
  { area_m2: 350, area_fan_kw: 3.5 },
  { area_m2: 400, area_fan_kw: 4.0 },
  { area_m2: 450, area_fan_kw: 4.5 },
  { area_m2: 500, area_fan_kw: 5.0 },
];
const makeOptionValue = (areaM2, secondary) => `${areaM2}|${secondary}`;
const UNIT_AIRFLOW_OPTIONS = {
  FCU: FCU_AREA_CFM_ROWS.map(row => ({
    name: `${row.area_m2} m² / ${row.area_cfm.toLocaleString()} CFM`,
    value: makeOptionValue(row.area_m2, row.area_cfm),
    area_m2: row.area_m2,
    area_cfm: row.area_cfm,
  })),
  AHU: AHU_AREA_FAN_KW_ROWS.map(row => ({
    name: `${row.area_m2} m² / ${row.area_fan_kw.toFixed(1)} kW`,
    value: makeOptionValue(row.area_m2, row.area_fan_kw),
    area_m2: row.area_m2,
    area_fan_kw: row.area_fan_kw,
  })),
};
const NOX_AVAILABLE_OPTIONS = [
  { name: 'Yes', value: 'true' },
  { name: 'No', value: 'false' },
];
const RADAR_AVAILABLE_OPTIONS = [
  { name: 'No', value: 'false' },
  { name: 'Yes', value: 'true' },
];
const INACTIVE_DEVICE_NOTIFICATION_OPTIONS = [
  { name: 'No', value: 'false' },
  { name: 'Yes', value: 'true' },
];
const DEVICE_SELECTOR_REQUESTED_PARAMS = [
  'id',
  'name',
  'dist_ids',
  'cust_ids',
  'building_id',
  'floor_number',
  'area_id',
  'status',
];

const FLOOR_MAP_ACTION_WIDTH = '320px';
const FLOOR_MAP_ACTION_SX = {
  width: { xs: '100%', sm: FLOOR_MAP_ACTION_WIDTH },
  maxWidth: FLOOR_MAP_ACTION_WIDTH,
  height: '3rem',
  border: `1px solid ${brandColors.BLUE}`,
  borderRadius: 2,
  backgroundColor: '#fff',
  color: brandColors.BLUE,
  boxSizing: 'border-box',
  justifyContent: 'center',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': { backgroundColor: brandColors.BLUE, color: '#fff' },
};

const isEmptyValue = v => v === null || v === undefined || v === '';

const getDeviceTypeFields = deviceType => {
  const normalizedDeviceType = String(deviceType || '');
  const sensorModel = SENSOR_MODEL_OPTIONS.find(option => normalizedDeviceType.endsWith(option.value))?.value || 'SEN54';

  return {
    isSensor: normalizedDeviceType.startsWith('Sensor-') || normalizedDeviceType.includes('-sensor-'),
    isController: normalizedDeviceType.startsWith('Controller'),
    sensorModel,
  };
};

const getDeviceTypeFromFields = ({ isSensor, isController, sensorModel }) => {
  if (!isSensor && !isController) {
    return '';
  }

  if (isController) {
    return `Controller${isSensor ? '-sensor' : ''}-${sensorModel || 'SEN54'}`;
  }
  return `Sensor-${sensorModel || 'SEN54'}`;
};

const getValueOrDefault = value => {
  return value === null || value === undefined || value === '' ? '' : value;
};

const getCo2DeltaForOcp = device => device?.co2_delta_for_ocp ?? '';

const isHiddenInFloorMap = device => Number(device?.map_x_value) === -1 && Number(device?.map_y_value) === -1;

const getDisplayValue = value => {
  return value === null || value === undefined || value === '' ? '-Unassigned-' : value;
};

const getSelectionFromDevice = (unitType, device) => {
  if (!device) return '';
  if (unitType === 'AHU') {
    const match = UNIT_AIRFLOW_OPTIONS.AHU.find(
      option => Number(option.area_m2) === Number(device.area_m2) && Number(option.area_fan_kw) === Number(device.area_fan_kw),
    );
    return match?.value || '';
  }

  const match = UNIT_AIRFLOW_OPTIONS.FCU.find(
    option => Number(option.area_m2) === Number(device.area_m2) && Number(option.area_cfm) === Number(device.area_cfm),
  );
  return match?.value || '';
};

const getAreaMetricsFromSelection = (unitType, selectedAreaOption) => {
  if (unitType === 'AHU') {
    const match = UNIT_AIRFLOW_OPTIONS.AHU.find(option => option.value === selectedAreaOption);
    return {
      area_m2: match?.area_m2 ?? null,
      area_cfm: null,
      area_fan_kw: match?.area_fan_kw ?? null,
    };
  }

  const match = UNIT_AIRFLOW_OPTIONS.FCU.find(option => option.value === selectedAreaOption);
  return {
    area_m2: match?.area_m2 ?? null,
    area_cfm: match?.area_cfm ?? null,
    area_fan_kw: null,
  };
};

const formatDateTime = value => {
  if (!value) return '';
  const timestamp = typeof value === 'string' ? value.trim().replace(' ', 'T') : value;
  const date = new Date(typeof timestamp === 'string' ? `${timestamp.replace(/Z?$/i, 'Z')}` : timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const pad = num => String(num).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}, ${pad(date.getDate())}/${pad(
    date.getMonth() + 1,
  )}/${date.getFullYear()}`;
};

const findOptionValue = (options, device, fieldName) => {
  if (!device || !options.length) return '';

  if (fieldName === 'building') {
    const byId = options.find(opt => opt.value === device.building_id?.toString());
    const byName = options.find(opt => opt.name === device.building);
    return byId?.value || byName?.value || '';
  }

  if (fieldName === 'floor') {
    const byFloor = options.find(opt => opt.value === device.floor_number?.toString());
    return byFloor?.value || '';
  }

  if (fieldName === 'area') {
    const byId = options.find(opt => opt.value === device.area_id?.toString());
    const byName = options.find(opt => opt.name === device.area_name);
    return byId?.value || byName?.value || 'Select';
  }

  return '';
};

function findFloorId(buildings, watchEditBuildingId, selectedFloorObj) {
  // 1) find the building
  const b = (buildings || []).find(bl => String(bl?.id ?? bl?.building_id) === String(watchEditBuildingId));
  if (!b || !Array.isArray(b.floors)) return null;

  // 2) extract the target floor number (handles 0 safely)
  const getTargetFloorNum = frf => {
    if (!frf) return NaN;
    if (frf.value != null && frf.value !== '') return Number(frf.value);
    if (frf.label != null && frf.label !== '') return Number(frf.label);
    if (typeof frf.name === 'string') {
      const m = frf.name.match(/-?\d+/); // e.g. "Floor 0" -> 0
      if (m) return Number(m[0]);
    }
    return NaN;
  };

  const targetNum = getTargetFloorNum(selectedFloorObj);
  if (Number.isNaN(targetNum)) return null;

  // 3) find the floor object whose "floor" equals that number
  const floorObj = b.floors.find(f => Number(f?.floor) === targetNum);

  return floorObj ? String(floorObj.id) : null;
}

function resolveAreaContext(buildings, areaId) {
  if (!areaId) return null;
  const normalizedAreaId = String(areaId);
  const buildingsList = Array.isArray(buildings) ? buildings : [];
  const normalizeList = list => (Array.isArray(list) ? list : Object.values(list || {}));

  for (const building of buildingsList) {
    const areas = normalizeList(building?.areas);
    const floors = normalizeList(building?.floors);
    const buildingId = building?.id ?? building?.building_id;
    const buildingName = building?.name ?? building?.building_name;

    const areaMatch = areas.find(area => String(area?.id) === normalizedAreaId);
    if (areaMatch) {
      const floorNumber = areaMatch?.floor ?? areaMatch?.floor_number ?? areaMatch?.floorNum ?? areaMatch?.floor_num;
      return {
        building_id: buildingId,
        building: buildingName,
        floor_number: floorNumber ?? '',
        area_name: areaMatch?.name ?? areaMatch?.area_name ?? areaMatch?.label ?? '',
      };
    }

    const floorMatch = floors.find(floor => String(floor?.id) === normalizedAreaId);
    if (floorMatch) {
      return {
        building_id: buildingId,
        building: buildingName,
        floor_number: floorMatch?.floor ?? floorMatch?.floor_number ?? floorMatch?.num ?? '',
        area_name: 'floor',
        is_full_floor: true,
      };
    }
  }

  return null;
}

const LocationPlacementSection = memo(function LocationPlacementSection({
  control,
  setValue,
  errors,
  buildings,
  users,
  selectedDevice,
  resolvedAreaContext,
  isControllerType,
  isSensorType,
  selectedSensorIds,
  setSelectedSensorIds,
  useCalculatedSensing,
  setUseCalculatedSensing,
  setSelectedFloorId,
  sectionCardSx,
  sectionTitleSx,
  isEmptyValue,
}) {
  const dispatch = useDispatch();
  const selectedFilterObject = useSelector(state => state.persistence.selectedFilterObject);
  const buildingSensorsList = useSelector(state => state.sensors.filteredDevices);
  const [buildingId, floorNumber, hideInFloorMap] = useWatch({
    control,
    name: ['building_id', 'floor_number', 'hide_in_floor_map'],
  });
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showDeviceSelectModal, setShowDeviceSelectModal] = useState(false);
  const [sourceSensorsResetDialog, setSourceSensorsResetDialog] = useState({ open: false, nextBuildingId: '' });
  const [hvacEquipment, setHvacEquipment] = useState([]);
  const [hvacEquipmentLoadError, setHvacEquipmentLoadError] = useState(false);
  const [controllerHvacMoveDialogOpen, setControllerHvacMoveDialogOpen] = useState(false);

  const filterOptions = useMemo(() => {
    if (!buildings || !users) return { buildings: [] };
    return generateManagementFilterOptions(users, buildings, { building: buildingId, floor: floorNumber });
  }, [buildings, users, buildingId, floorNumber]);
  const buildingOptions = useMemo(
    () =>
      (filterOptions.buildings || []).map(building => ({
        value: building.value,
        name: building.label ?? building.name ?? String(building.value ?? ''),
        id: building.id ?? building.value,
      })),
    [filterOptions.buildings],
  );
  const floorOptions = useMemo(() => {
    const targetBuildingId = buildingId || resolvedAreaContext?.building_id;
    const building = (buildings || []).find(bl => String(bl?.id ?? bl?.building_id) === String(targetBuildingId));
    const floors = Array.isArray(building?.floors) ? building.floors : Object.values(building?.floors || {});
    return Array.from(new Map(floors.map(floor => [floor?.floor, floor])).values())
      .sort((a, b) => Number(a.floor) - Number(b.floor))
      .map(floor => ({ value: String(floor.floor ?? ''), name: `Floor ${floor.floor ?? ''}`, id: floor.id ?? floor.floor }))
      .filter(option => option.value !== '');
  }, [buildings, buildingId, resolvedAreaContext]);
  const areaOptions = useMemo(() => {
    const targetBuildingId = buildingId || resolvedAreaContext?.building_id;
    const targetFloorNumber = floorNumber || resolvedAreaContext?.floor_number;
    const building = (buildings || []).find(bl => String(bl?.id ?? bl?.building_id) === String(targetBuildingId));
    const areas = Array.isArray(building?.areas) ? building.areas : Object.values(building?.areas || {});
    return areas
      .filter(area => !targetFloorNumber || String(area?.floor) === String(targetFloorNumber))
      .map(area => ({ value: String(area?.id ?? ''), name: area?.name ?? area?.area_name ?? area?.label ?? '', id: area?.id }))
      .filter(option => option.value !== '');
  }, [buildings, buildingId, floorNumber, resolvedAreaContext]);
  const selectedBuildingId = String(buildingId || resolvedAreaContext?.building_id || selectedDevice?.building_id || '');

  // Reload equipment whenever the selected building changes.
  useEffect(() => {
    if (!selectedBuildingId) {
      setHvacEquipment([]);
      setHvacEquipmentLoadError(false);
      return;
    }
    let mounted = true;
    getHvacEquipment(selectedBuildingId)
      .then(items => {
        if (!mounted) return;
        setHvacEquipment(items);
        // Controller assignments are stored in each equipment record's controller_ids list.
        // Preselect the equipment records that already contain this controller.
        setValue(
          'controller_hvac_equipment_ids',
          items
            .filter(item => Array.isArray(item.controller_ids) && item.controller_ids.some(id => String(id) === String(selectedDevice?.id)))
            .map(item => String(item.id)),
          { shouldDirty: false },
        );
        setHvacEquipmentLoadError(false);
      })
      .catch(() => {
        if (!mounted) return;
        setHvacEquipment([]);
        setHvacEquipmentLoadError(true);
      });
    return () => {
      mounted = false;
    };
  }, [selectedBuildingId, selectedDevice?.id, setValue]);
  const floorSchemaKey = useMemo(() => {
    const building = (buildings || []).find(bl => String(bl?.id ?? bl?.building_id) === selectedBuildingId);
    const floors = Array.isArray(building?.floors) ? building.floors : Object.values(building?.floors || {});
    return (
      floors.find(floor => String(floor?.floor ?? floor?.floor_number ?? floor?.num) === String(floorNumber))?.scheme ?? null
    );
  }, [buildings, selectedBuildingId, floorNumber]);

  useEffect(() => {
    if (isEmptyValue(buildingId) || !floorOptions.length || !isEmptyValue(floorNumber)) return;
    const firstFloor = floorOptions[0];
    setValue('floor_number', String(firstFloor.value), { shouldDirty: false, shouldValidate: true });
    setValue('floor_or_area_id', '');
    setSelectedFloorId(findFloorId(buildings, buildingId, firstFloor));
  }, [buildings, buildingId, floorNumber, floorOptions, isEmptyValue, setSelectedFloorId, setValue]);

  const refreshDevicesForBuilding = useCallback(
    async nextBuildingId => {
      const normalizedBuildingId = String(nextBuildingId || '');
      if (!normalizedBuildingId) return;
      dispatch(setSelectedBuilding(normalizedBuildingId));
      const nextFilters = {
        distributor: selectedFilterObject?.distributor || 'all',
        customer: selectedFilterObject?.customer || 'all',
        building: normalizedBuildingId,
        floor: 'all',
        area: 'all',
        status: 'active',
        searchString: selectedFilterObject?.searchString || '',
      };
      dispatch(setSearchString(nextFilters.searchString));
      await dispatch(
        fetchUserDevices({
          devicesType: 'all',
          buildings_ids_list: [normalizedBuildingId],
          requestedParams: DEVICE_SELECTOR_REQUESTED_PARAMS,
        }),
      ).unwrap();
      dispatch(updateFilteredDevicesWithSync(nextFilters));
    },
    [dispatch, selectedFilterObject],
  );

  const applyBuildingChange = useCallback(
    nextBuildingId => {
      setValue('floor_number', '');
      setValue('floor_or_area_id', '');
      setValue('area_id', '');
      // Equipment belongs to the old building, so clear it before selecting the new one.
      setValue('hvac_equipment_ids', []);
      setValue('controller_hvac_equipment_ids', []);
      setValue('building_id', nextBuildingId, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  const openDeviceSelector = useCallback(() => {
    const nextFilters = {
      distributor: selectedFilterObject?.distributor || 'all',
      customer: selectedFilterObject?.customer || 'all',
      building: selectedBuildingId || 'all',
      floor: 'all',
      area: 'all',
      status: 'active',
      searchString: selectedFilterObject?.searchString || '',
    };
    dispatch(setSearchString(nextFilters.searchString));
    dispatch(updateFilteredDevicesWithSync(nextFilters));
    setShowDeviceSelectModal(true);
  }, [dispatch, selectedBuildingId, selectedFilterObject]);

  return (
    <Grid item xs={12}>
      <Typography sx={sectionTitleSx}>
        <LocationOnOutlinedIcon sx={{ color: '#6a5ae0' }} />
        Location & Placement
      </Typography>
      <Box sx={sectionCardSx}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Controller
              name="building_id"
              control={control}
              render={({ field }) => (
                <DropdownMenu
                  options={buildingOptions.length ? buildingOptions : [{ name: 'No buildings available', value: '' }]}
                  selectedOption={field.value ?? ''}
                  labelName="Building"
                  emptyLabel="Select"
                  disabled={!buildingOptions.length}
                  error={!!errors.building_id}
                  inline
                  labelWidth="15rem"
                  handleChange={event => {
                    const nextBuildingId = event.target.value;
                    const currentBuildingId = field.value || selectedDevice?.building_id;
                    const isCurrentController =
                      isControllerType || String(selectedDevice?.device_type || '').startsWith('Controller');
                    const controlsHvacEquipment = hvacEquipment.some(equipment =>
                      Array.isArray(equipment.controller_ids) &&
                      equipment.controller_ids.some(controllerId => String(controllerId) === String(selectedDevice?.id)),
                    );
                    if (
                      isCurrentController &&
                      controlsHvacEquipment &&
                      String(currentBuildingId) !== String(nextBuildingId)
                    ) {
                      // Show the rule before submitting. The backend checks it again when saving.
                      setControllerHvacMoveDialogOpen(true);
                      return;
                    }
                    const hasSourceSensors =
                      isControllerType &&
                      (selectedSensorIds.length > 0 ||
                        useCalculatedSensing ||
                        (selectedDevice?.source_sensors_ids || []).length > 0 ||
                        (selectedDevice?.calculated_decision || []).length > 0);
                    if (
                      hasSourceSensors &&
                      !isEmptyValue(currentBuildingId) &&
                      String(currentBuildingId) !== String(nextBuildingId)
                    ) {
                      setSourceSensorsResetDialog({ open: true, nextBuildingId });
                      return;
                    }
                    applyBuildingChange(nextBuildingId);
                    if (String(currentBuildingId) !== String(nextBuildingId)) refreshDevicesForBuilding(nextBuildingId);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="floor_number"
              control={control}
              render={({ field }) => {
                const options = !buildingId
                  ? [{ name: 'Select building first', value: '' }]
                  : floorOptions.length
                    ? floorOptions
                    : [{ name: 'No floors available', value: '' }];
                return (
                  <DropdownMenu
                    disabled={!buildingId || !floorOptions.length}
                    options={options}
                    selectedOption={field.value || ''}
                    labelName="Floor"
                    error={!!errors.floor_number}
                    inline
                    labelWidth="15rem"
                    handleChange={event => {
                      const option = options.find(item => String(item.value) === String(event.target.value));
                      setValue('floor_or_area_id', option?.id || '');
                      field.onChange(event);
                    }}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="floor_or_area_id"
              control={control}
              render={({ field }) => {
                const hasFloor = !isEmptyValue(floorNumber);
                const options = !hasFloor
                  ? [{ name: 'Select floor first', value: '' }]
                  : areaOptions.length
                    ? areaOptions
                    : [{ name: 'No areas available', value: '' }];
                return (
                  <DropdownMenu
                    disabled={!hasFloor || !areaOptions.length}
                    options={options}
                    selectedOption={isEmptyValue(field.value) ? '' : String(field.value)}
                    handleChange={field.onChange}
                    labelName="Area"
                    error={!!errors.area_id}
                    inline
                    labelWidth="15rem"
                  />
                );
              }}
            />
          </Grid>
          {isSensorType && (
            <Grid item xs={12} md={6}>
              <Controller
                name="hvac_equipment_ids"
                control={control}
                render={({ field }) => {
                  const selectedIds = Array.isArray(field.value) ? field.value.map(String) : [];
                  return (
                    <DropdownMenu
                      // Use the shared dropdown so this field matches Building, Floor, Area, and Space.
                      multiple
                      disabled={!selectedBuildingId || hvacEquipmentLoadError || hvacEquipment.length === 0}
                      options={hvacEquipment.map(item => ({
                        value: String(item.id),
                        name: `${item.name} (${item.equipment_type})`,
                      }))}
                      selectedOption={selectedIds}
                      handleChange={event => field.onChange(event.target.value.map(String))}
                      labelName="HVAC equipment (sensor)"
                      emptyLabel="Select"
                      inline
                      labelWidth="15rem"
                      renderSelected={selected =>
                        selected.length
                          ? `${selected.length} selected`
                          : (
                            <em>
                              {hvacEquipmentLoadError
                                ? 'Could not load HVAC equipment'
                                : !selectedBuildingId
                                  ? 'Select building first'
                                  : hvacEquipment.length === 0
                                    ? 'No HVAC equipment'
                                    : 'Select'}
                            </em>
                          )
                      }
                    />
                  );
                }}
              />
            </Grid>
          )}
          {isControllerType && (
            <Grid item xs={12} md={6}>
              <Controller
                name="controller_hvac_equipment_ids"
                control={control}
                render={({ field }) => {
                  const selectedIds = Array.isArray(field.value) ? field.value.map(String) : [];
                  return (
                    <DropdownMenu
                      multiple
                      disabled={!selectedBuildingId || hvacEquipmentLoadError || hvacEquipment.length === 0}
                      options={hvacEquipment.map(item => ({
                        value: String(item.id),
                        name: `${item.name} (${item.equipment_type})`,
                      }))}
                      selectedOption={selectedIds}
                      handleChange={event => field.onChange(event.target.value.map(String))}
                      labelName="HVAC equipment (controller)"
                      emptyLabel="Select"
                      inline
                      labelWidth="15rem"
                      renderSelected={selected =>
                        selected.length
                          ? `${selected.length} selected`
                          : (
                            <em>
                              {hvacEquipmentLoadError
                                ? 'Could not load HVAC equipment'
                                : !selectedBuildingId
                                  ? 'Select building first'
                                  : hvacEquipment.length === 0
                                    ? 'No HVAC equipment'
                                    : 'Select'}
                            </em>
                          )
                      }
                    />
                  );
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <Controller
              name="space"
              control={control}
              render={({ field }) => (
                <DropdownMenu
                  options={SPACE_OPTIONS}
                  selectedOption={field.value || ''}
                  handleChange={field.onChange}
                  labelName="Space"
                  error={!!errors.space}
                  inline
                  labelWidth="15rem"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.75rem' }}>
            <Button
              className="one-click-btn"
              variant="inherit"
              onClick={() => setLocationModalOpen(true)}
              disabled={hideInFloorMap}
              sx={{ ...FLOOR_MAP_ACTION_SX, gap: '.8rem' }}>
              set the device floor location <EditLocationAltIcon sx={{ scale: '2' }} />
            </Button>
            <Controller
              name="hide_in_floor_map"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={event => {
                        field.onChange(event.target.checked);
                        if (event.target.checked) {
                          setValue('map_x_value', -1);
                          setValue('map_y_value', -1);
                        }
                      }}
                      sx={{ color: brandColors.BLUE, p: 0, mr: 1, '&.Mui-checked': { color: brandColors.BLUE } }}
                    />
                  }
                  label={
                    <Box component="span" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                      HIDE DEVICE IN FLOOR MAP <LocationOffIcon sx={{ scale: '2', ml: 1 }} />
                    </Box>
                  }
                  sx={{ ...FLOOR_MAP_ACTION_SX, m: 0, px: 1.5, py: 0 }}
                />
              )}
            />
          </Grid>
          {isControllerType && (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                className="one-click-btn"
                variant="inherit"
                color="primary"
                onClick={openDeviceSelector}
                sx={{ ...FLOOR_MAP_ACTION_SX, gap: '1rem' }}>
                Collective Decision Mode <HubIcon sx={{ scale: '2' }} />
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
      {isControllerType && (
        <DevicesSelect
          open={showDeviceSelectModal}
          onClose={() => setShowDeviceSelectModal(false)}
          buildingSensors={buildingSensorsList}
          forcedBuildingId={selectedBuildingId}
          selectedDevices={selectedSensorIds}
          onSubmit={setSelectedSensorIds}
          tooltipText="Aggregates occupancy levels from the selected sensors to calculate this controller’s occupancy level."
        />
      )}
      {locationModalOpen && (
        <GenericModal
          open={locationModalOpen}
          onClose={() => setLocationModalOpen(false)}
          title="Set device location"
          Component={DeviceLocation}
          componentProps={{
            floorSchemaKey,
            onPositionSelect: ({ x, y }) => {
              setValue('hide_in_floor_map', false, { shouldDirty: true, shouldValidate: true });
              setValue('map_x_value', Number(x.toFixed(2)), { shouldDirty: true, shouldValidate: true });
              setValue('map_y_value', Number(y.toFixed(2)), { shouldDirty: true, shouldValidate: true });
            },
          }}
          paperSx={{
            width: 'min(1560px, 99vw)',
            maxWidth: '99vw',
            height: '90vh',
            maxHeight: '99vh',
            display: 'flex',
            flexDirection: 'column',
          }}
          contentProps={{ sx: { p: 1, flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' } }}
        />
      )}
      <AlertDialogSlide
        open={sourceSensorsResetDialog.open}
        title="Collective mode source sensors"
        content="This controller has collective mode enabled. Changing the building will reset the source sensors list."
        handleClose={() => setSourceSensorsResetDialog({ open: false, nextBuildingId: '' })}
        handleOk={() => {
          const nextBuildingId = sourceSensorsResetDialog.nextBuildingId;
          setSelectedSensorIds([]);
          setUseCalculatedSensing(false);
          applyBuildingChange(nextBuildingId);
          refreshDevicesForBuilding(nextBuildingId);
          setSourceSensorsResetDialog({ open: false, nextBuildingId: '' });
        }}
      />
      <ControllerHvacMoveDialog
        open={controllerHvacMoveDialogOpen}
        onClose={() => setControllerHvacMoveDialogOpen(false)}
      />
    </Grid>
  );
});

const DeviceForm = () => {
  // Router + redux hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { deviceId, deviceType } = useParams();
  const isControllerFromRoute = deviceType === 'controller';

  // Redux selectors
  const buildings = useSelector(state => state.sensors.buildings_details);
  const currentUser = useSelector(state => state.sensors.currentUser);
  const users = useSelector(state => state.users.users);

  // Local UI state
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const selectedFloorIdRef = useRef('');
  const [show, setShow] = useState(false);
  const [useCalculatedSensing, setUseCalculatedSensing] = useState(false);
  const [selectedSensorIds, setSelectedSensorIds] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Form management for Edit Device
  const {
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    control: controlEdit,
    watch: watchEdit,
    setValue: setValueEdit,
    formState: { errors: errorsEdit },
  } = useForm({
    defaultValues: {
      deviceId: selectedDevice?.id ?? '',
      device_name: selectedDevice?.name ?? '',
      device_type: selectedDevice?.device_type ?? 'Sensor-SEN54',
      device_is_sensor: getDeviceTypeFields(selectedDevice?.device_type ?? 'Sensor-SEN54').isSensor,
      device_is_controller: getDeviceTypeFields(selectedDevice?.device_type ?? 'Sensor-SEN54').isController,
      sensor_model: getDeviceTypeFields(selectedDevice?.device_type ?? 'Sensor-SEN54').sensorModel,
      has_nox: selectedDevice?.has_nox === true ? 'true' : selectedDevice?.has_nox === false ? 'false' : '',
      inactive_device_notification_enabled:
        selectedDevice?.inactive_device_notification_enabled === true
          ? 'true'
          : selectedDevice?.inactive_device_notification_enabled === false
            ? 'false'
            : 'false',
      is_using_radar:
        selectedDevice?.is_using_radar === true ? 'true' : selectedDevice?.is_using_radar === false ? 'false' : 'false',
      co2_delta_for_ocp: getCo2DeltaForOcp(selectedDevice),
      unit_type: selectedDevice?.unit_type ?? '',
      cooling_type: selectedDevice?.cooling_type ?? '',
      heating_type: selectedDevice?.heating_type ?? '',
      area_option: getSelectionFromDevice(selectedDevice?.unit_type, selectedDevice),
      space: selectedDevice?.space ?? '',
      building_id: selectedDevice?.building_id ?? '',
      floor_or_area_id: selectedDevice?.is_full_floor ? 'Floor' : String(selectedDevice?.area_id ?? ''),
      floor_number: selectedDevice?.floor_number ?? '',
      slave_id: selectedDevice?.slave_id ?? '',
      map_x_value: selectedDevice?.map_x_value ?? '',
      map_y_value: selectedDevice?.map_y_value ?? '',
        hide_in_floor_map: isHiddenInFloorMap(selectedDevice),
        // Select values are strings in the form. The saved IDs can arrive as numbers.
        hvac_equipment_ids: Array.isArray(selectedDevice?.hvac_equipment_ids) ? selectedDevice.hvac_equipment_ids.map(String) : [],
        controller_hvac_equipment_ids: Array.isArray(selectedDevice?.controller_hvac_equipment_ids)
          ? selectedDevice.controller_hvac_equipment_ids.map(String)
          : [],
    },
  });

  // Watch values for cascading dropdowns
  const watchEditUnitType = watchEdit('unit_type');
  const watchEditHeatingType = watchEdit('heating_type');
  const watchEditDeviceType = watchEdit('device_type');
  const watchEditDeviceIsSensor = watchEdit('device_is_sensor');
  const watchEditDeviceIsController = watchEdit('device_is_controller');
  const watchEditSensorModel = watchEdit('sensor_model');
  const watchEditControllerModel = watchEdit('controller_model');
  const watchEditAreaOption = watchEdit('area_option');

  const isSelectedController = (selectedDevice?.device_type || '').startsWith('Controller');
  const isControllerType = !!watchEditDeviceIsController;
  const canManageUnitConfiguration = currentUser?.role === 's' || currentUser?.role === 'd';
  const canGoBack = typeof window !== 'undefined' && Number(window.history.state?.idx) > 0;

  // Derived data
  const resolvedAreaContext = useMemo(
    () => resolveAreaContext(buildings, selectedDevice?.area_id),
    [buildings, selectedDevice?.area_id],
  );

  // These options are only used to restore the initially loaded device values.
  const filterOptions = useMemo(() => {
    if (!buildings || !users) return { buildings: [], floors: [], areas: [] };
    return generateManagementFilterOptions(users, buildings, {
      building: resolvedAreaContext?.building_id || selectedDevice?.building_id,
      floor: resolvedAreaContext?.floor_number || selectedDevice?.floor_number,
    });
  }, [buildings, users, resolvedAreaContext, selectedDevice]);

  // Convert to legacy format for DropdownMenu component
  const buildingOptions = useMemo(() => {
    const options = (filterOptions.buildings || []).map(building => ({
      value: building.value,
      name: building.label ?? building.name ?? String(building.value ?? ''),
      id: building.id ?? building.value,
    }));
    return options.length > 0 ? options : []; // Exclude "All Buildings" option for editing
  }, [filterOptions.buildings]);

  const floorOptions = useMemo(() => {
    const targetBuildingId = resolvedAreaContext?.building_id || selectedDevice?.building_id;
    if (!targetBuildingId) return [];

    const building = (buildings || []).find(bl => String(bl?.id ?? bl?.building_id) === String(targetBuildingId));
    const floorsList = Array.isArray(building?.floors) ? building.floors : Object.values(building?.floors || {});
    const uniqueFloors = Array.from(new Map((floorsList || []).map(floor => [floor?.floor, floor])).values());
    const options = uniqueFloors
      .sort((a, b) => Number(a.floor) - Number(b.floor))
      .map(floor => ({
        value: String(floor.floor ?? ''),
        name: `Floor ${floor.floor ?? ''}`,
        id: floor.id ?? floor.floor,
      }))
      .filter(opt => opt.value !== '');
    return options.length > 0 ? options : [];
  }, [buildings, resolvedAreaContext, selectedDevice?.building_id]);

  const areaOptions = useMemo(() => {
    const targetBuildingId = resolvedAreaContext?.building_id || selectedDevice?.building_id;
    const targetFloorNumber = resolvedAreaContext?.floor_number || selectedDevice?.floor_number;
    if (!targetBuildingId) return [];

    const building = (buildings || []).find(bl => String(bl?.id ?? bl?.building_id) === String(targetBuildingId));
    const areasList = Array.isArray(building?.areas) ? building.areas : Object.values(building?.areas || {});
    const filteredAreas = targetFloorNumber
      ? (areasList || []).filter(a => String(a?.floor) === String(targetFloorNumber))
      : areasList || [];
    const options = (filteredAreas || [])
      .map(area => ({
        value: String(area?.id ?? ''),
        name: area?.name ?? area?.area_name ?? area?.label ?? '',
        id: area?.id ?? area?.value,
      }))
      .filter(opt => opt.value !== '');
    return options.length > 0 ? options : [];
  }, [buildings, resolvedAreaContext, selectedDevice?.building_id, selectedDevice?.floor_number]);
  const selectedDeviceId = selectedDevice?.id;
  // Kept only for the previous location markup below while it is disabled during the section extraction.
  const loading = false;
  const watchEditBuildingId = '';
  const watchEditFloorNumber = '';
  const watchHideInFloorMap = false;
  const locationModalOpen = false;
  const showDeviceSelectModal = false;
  const sourceSensorsResetDialog = { open: false, nextBuildingId: '' };
  const buildingSensorsList = [];
  const deviceFormSelectedBuildingId = '';
  const setLocationModalOpen = () => {};
  const setSourceSensorsResetDialog = () => {};
  const refreshDevicesForBuilding = () => {};
  const handleSelectDevicesClick = () => {};
  const handleCloseDeviceSelectModal = () => {};
  const handleDeviceSelectSubmit = () => {};
  const handleLocationModalClose = () => {};
  const handleSourceSensorsResetDialogClose = () => {};
  const handleSourceSensorsResetDialogOk = () => {};
  const floorSchemaKey = null;

  // Effects
  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        if (!deviceId) return;
        const deviceData = await dispatch(fetchDeviceInfo({ deviceId: deviceId })).unwrap();
        dispatch(setDevicesList(deviceData.building_devices));
        setSelectedDevice(deviceData);
        setValueEdit('space', deviceData?.space ?? '');
        setValueEdit(
          'is_using_radar',
          deviceData?.is_using_radar === true ? 'true' : deviceData?.is_using_radar === false ? 'false' : 'false',
        );
        setValueEdit(
          'inactive_device_notification_enabled',
          deviceData?.inactive_device_notification_enabled === true
            ? 'true'
            : deviceData?.inactive_device_notification_enabled === false
              ? 'false'
              : 'false',
        );
        setSelectedSensorIds(Array.isArray(deviceData.source_sensors_ids) ? deviceData.source_sensors_ids : []);
        if (!Array.isArray(buildings) || buildings.length === 0) {
          dispatch(getBuildings());
        }
      } catch (error) {
        console.error('❌ DeviceForm: Failed to initialize data:', error);
        if (error?.statusCode === 403) {
          navigate('/unauthorized', { replace: true });
          return;
        }

        setNotification({
          open: true,
          message: 'Failed to load device data. Please refresh the page.',
          severity: 'error',
        });
      }
    };

    initializeData();
  }, [dispatch, deviceId, setValueEdit]);

  useEffect(() => {
    if (!selectedDevice || !selectedDevice.area_id || !buildings || buildings.length === 0) return;

    const resolved = resolvedAreaContext;
    if (!resolved) return;

    setSelectedDevice(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...resolved };
      const didChange =
        String(prev.building_id ?? '') !== String(next.building_id ?? '') ||
        String(prev.building ?? '') !== String(next.building ?? '') ||
        String(prev.floor_number ?? '') !== String(next.floor_number ?? '') ||
        String(prev.area_name ?? '') !== String(next.area_name ?? '');
      return didChange ? next : prev;
    });
  }, [buildings, selectedDevice?.area_id]);

  // Reset form to original device values
  const handleReset = () => {
    if (selectedDevice) {
      // Reset the flag to allow auto-fill to run again
      setIsInitialLoad(true);

      if (isSelectedController) {
        const sourceSensorIds = Array.isArray(selectedDevice.source_sensors_ids) ? selectedDevice.source_sensors_ids : [];
        const hasCalculatedDecision =
          sourceSensorIds.length > 0 && (sourceSensorIds.length > 1 || String(sourceSensorIds[0]) !== deviceId);
        setUseCalculatedSensing(hasCalculatedDecision);
        setSelectedSensorIds(hasCalculatedDecision ? sourceSensorIds : []);
      }

      // Create the original form values object
      const originalValues = {
        device_name: getValueOrDefault(selectedDevice.name),
        device_type: getValueOrDefault(selectedDevice.device_type) || 'Sensor-SEN54',
        device_is_sensor: getDeviceTypeFields(selectedDevice.device_type).isSensor,
        device_is_controller: getDeviceTypeFields(selectedDevice.device_type).isController,
        sensor_model: getDeviceTypeFields(selectedDevice.device_type).sensorModel,
        has_nox: selectedDevice?.has_nox === true ? 'true' : selectedDevice?.has_nox === false ? 'false' : '',
        inactive_device_notification_enabled:
          selectedDevice?.inactive_device_notification_enabled === true
            ? 'true'
            : selectedDevice?.inactive_device_notification_enabled === false
              ? 'false'
              : 'false',
        is_using_radar:
          selectedDevice?.is_using_radar === true ? 'true' : selectedDevice?.is_using_radar === false ? 'false' : 'false',
        co2_delta_for_ocp: getValueOrDefault(getCo2DeltaForOcp(selectedDevice)),
        unit_type: getValueOrDefault(selectedDevice.unit_type),
        cooling_type: getValueOrDefault(selectedDevice.cooling_type),
        heating_type: getValueOrDefault(selectedDevice.heating_type),
        area_option: getSelectionFromDevice(selectedDevice.unit_type, selectedDevice),
        space: getValueOrDefault(selectedDevice.space),
        controller_model: getValueOrDefault(selectedDevice.controller_model) || 'CTU4501-CP-HP-SUPER-ST-190714',
        map_x_value: getValueOrDefault(selectedDevice.map_x_value),
        map_y_value: getValueOrDefault(selectedDevice.map_y_value),
        hide_in_floor_map: isHiddenInFloorMap(selectedDevice),
        // Keep the saved HVAC choices when reopening the form.
        hvac_equipment_ids: Array.isArray(selectedDevice.hvac_equipment_ids) ? selectedDevice.hvac_equipment_ids.map(String) : [],
        controller_hvac_equipment_ids: Array.isArray(selectedDevice.controller_hvac_equipment_ids)
          ? selectedDevice.controller_hvac_equipment_ids.map(String)
          : [],
        building_id:
          resolvedAreaContext?.building_id ||
          findOptionValue(buildingOptions, selectedDevice, 'building') ||
          getValueOrDefault(selectedDevice.building_id),
        floor_number:
          resolvedAreaContext?.floor_number ||
          findOptionValue(floorOptions, selectedDevice, 'floor') ||
          getValueOrDefault(selectedDevice.floor_number),
        floor_or_area_id: resolvedAreaContext?.is_full_floor
          ? 'Floor'
          : findOptionValue(areaOptions, selectedDevice, 'area') || getValueOrDefault(selectedDevice.area_id),
        slave_id: selectedDevice.slave_id,
      };

      // Reset the form with original values
      resetEdit(originalValues);
    }
  };

  // Auto-fill form when device is selected from URL
  const didInitForm = useRef(false);

  useEffect(() => {
    if (didInitForm.current) return;
    if (!selectedDevice) return;
    if (buildingOptions.length === 0) return;

    handleReset();
    didInitForm.current = true;
  }, [selectedDevice, buildingOptions.length, areaOptions.length, handleReset]);

  useEffect(() => {
    if (!selectedDevice) return;

    const nextIds = Array.isArray(selectedDevice.source_sensors_ids)
      ? selectedDevice.source_sensors_ids
      : Array.isArray(selectedDevice.calculated_decision)
        ? selectedDevice.calculated_decision
        : [];

    setUseCalculatedSensing(prev => (prev === nextIds.length > 0 ? prev : nextIds.length > 0));
    // setSelectedSensorIds(prev => (sameArray(prev, nextIds) ? prev : nextIds));
  }, [selectedDevice]);

  useEffect(() => {
    const t = setTimeout(() => setShow(true), 2000); // 2s delay
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const allowedHeatingValues = (watchEditUnitType === 'AHU' ? HEATING_TYPE_OPTIONS.AHU : HEATING_TYPE_OPTIONS.FCU).map(
      option => option.value,
    );
    if (!isEmptyValue(watchEditHeatingType) && !allowedHeatingValues.includes(watchEditHeatingType)) {
      setValueEdit('heating_type', '', { shouldDirty: true, shouldValidate: true });
    }
  }, [setValueEdit, watchEditHeatingType, watchEditUnitType]);

  useEffect(() => {
    const allowedOptions = (watchEditUnitType === 'AHU' ? UNIT_AIRFLOW_OPTIONS.AHU : UNIT_AIRFLOW_OPTIONS.FCU).map(
      option => option.value,
    );
    if (!isEmptyValue(watchEditAreaOption) && !allowedOptions.includes(watchEditAreaOption)) {
      setValueEdit('area_option', '', { shouldDirty: true, shouldValidate: true });
    }
  }, [setValueEdit, watchEditAreaOption, watchEditUnitType]);

  useEffect(() => {
    if (!watchEditDeviceIsSensor && !watchEditDeviceIsController) {
      setValueEdit('device_is_sensor', true, { shouldValidate: true });
    }
  }, [setValueEdit, watchEditDeviceIsController, watchEditDeviceIsSensor]);

  useEffect(() => {
    const nextDeviceType = getDeviceTypeFromFields({
      isSensor: watchEditDeviceIsSensor,
      isController: watchEditDeviceIsController,
      sensorModel: watchEditSensorModel,
    });
    if (nextDeviceType && nextDeviceType !== watchEditDeviceType) {
      setValueEdit('device_type', nextDeviceType, { shouldDirty: true, shouldValidate: true });
    }
  }, [setValueEdit, watchEditDeviceIsController, watchEditDeviceIsSensor, watchEditDeviceType, watchEditSensorModel]);

  // A controller-only device cannot retain the hidden sensor-equipment relationship.
  useEffect(() => {
    if (!watchEditDeviceIsSensor) {
      setValueEdit('hvac_equipment_ids', [], { shouldDirty: true });
    }
  }, [setValueEdit, watchEditDeviceIsSensor]);

  useEffect(() => {
    if (!isControllerType) return;

    const allowedControllerModelValues = CONTROLLER_MODEL_OPTIONS.map(option => option.value);
    const fallbackValue = CONTROLLER_MODEL_OPTIONS[0]?.value || '';

    if (!allowedControllerModelValues.includes(watchEditControllerModel || '')) {
      setValueEdit('controller_model', fallbackValue, { shouldDirty: true, shouldValidate: true });
    }
  }, [isControllerType, setValueEdit, watchEditControllerModel]);

  // Handlers
  const handleNotificationClose = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalOpen(false);
  }, []);

  // Handle Edit Device submission
  const onEditSubmit = async data => {
    try {
      const deviceType = getDeviceTypeFromFields({
        isSensor: data.device_is_sensor,
        isController: data.device_is_controller,
        sensorModel: data.sensor_model,
      });
      if (!deviceType) {
        setNotification({ open: true, message: 'Select Sensor, Controller, or both.', severity: 'error' });
        return;
      }

      if (data.floor_number === '' || !data.floor_number) {
        setNotification({
          open: true,
          message: 'Please select a building and a floor to the device',
          severity: 'error',
        });
        return;
      }

      const areaMetrics = getAreaMetricsFromSelection(data.unit_type, data.area_option);
      const mapXValue = data.hide_in_floor_map ? -1 : data.map_x_value;
      const mapYValue = data.hide_in_floor_map ? -1 : data.map_y_value;

      const device = {
        deviceId: deviceId,
        device_name: data.device_name,
        device_type: deviceType,
        has_nox: data.has_nox === 'true' ? true : data.has_nox === 'false' ? false : null,
        inactive_device_notification_enabled: data.inactive_device_notification_enabled === 'true',
        is_using_radar: data.is_using_radar === 'true',
        co2_delta_for_ocp: data.co2_delta_for_ocp === '' ? null : Number(data.co2_delta_for_ocp),
        unit_type: data.unit_type,
        area_m2: areaMetrics.area_m2,
        area_cfm: areaMetrics.area_cfm,
        area_fan_kw: areaMetrics.area_fan_kw,
        cooling_type: data.cooling_type,
        heating_type: data.heating_type,
        space: data.space,
        controller_model: deviceType.startsWith('Controller') ? data.controller_model || '' : '',
        building_id: data.building_id,
        floor_or_area_id: data.floor_or_area_id === '' ? selectedFloorIdRef.current : data.floor_or_area_id,
        floor_number: data.floor_number,
        map_x_value: mapXValue,
        map_y_value: mapYValue,
        slave_id: data.slave_id ?? null,
        // The API validates that every selected equipment ID belongs to this building.
        hvac_equipment_ids: Array.isArray(data.hvac_equipment_ids) ? data.hvac_equipment_ids.map(Number) : [],
        controller_hvac_equipment_ids: Array.isArray(data.controller_hvac_equipment_ids)
          ? data.controller_hvac_equipment_ids.map(Number)
          : [],
        // Send array of sensor IDs if enabled, empty array if disabled, null if sensor device
        source_sensors_ids: deviceType.startsWith('Controller') ? selectedSensorIds : null,
      };

      setIsSaving(true);
      // unwrap() will throw an error if the thunk was rejected
      await dispatch(updateDeviceValues(device)).unwrap();

      // Update the saved device data on this page after save.
      // This stops old source sensors from showing the warning again.
      setSelectedDevice(prev =>
        prev
          ? {
              ...prev,
              device_type: deviceType,
              building_id: data.building_id,
              floor_number: data.floor_number,
              area_id: device.floor_or_area_id,
              hvac_equipment_ids: device.hvac_equipment_ids,
              controller_hvac_equipment_ids: device.controller_hvac_equipment_ids,
              source_sensors_ids: selectedSensorIds.length > 0 ? selectedSensorIds : null,
            }
          : prev,
      );

      // If we get here, the thunk succeeded
      setNotification({
        open: true,
        message: 'Device updated successfully!',
        severity: 'success',
      });
    } catch (error) {
      // This will catch both network errors and logical errors from your API
      console.error('Update failed:', error);
      setNotification({
        open: true,
        message: error.message || 'An unexpected error occurred',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle Delete Device alert
  const handleDeleteClick = () => {
    if (!deviceId) {
      setNotification({
        open: true,
        message: 'No device selected for deletion.',
        severity: 'error',
      });
      return;
    }
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsSaving(true);
      await dispatch(deleteDeviceValues(deviceId)).unwrap();

      const targetRoute = isSelectedController || isControllerFromRoute ? '/manage-controllers' : '/manage-sensors';

      navigate(targetRoute, {
        state: {
          notification: {
            open: true,
            message: 'Device and data deleted successfully!',
            severity: 'success',
          },
        },
      });
    } catch (error) {
      // catch both network errors and logical errors from your API
      console.error('Delete failed:', error);
      setNotification({
        open: true,
        message: error.message || 'An unexpected error occurred',
        severity: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const sectionTitleSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    fontWeight: 600,
    color: '#202432',
    fontSize: '1.5rem',
    mb: 1,
    mt: 1.5,
  };
  const sectionCardSx = {
    backgroundColor: '#e6e9f2',
    border: '1px solid #e6e9f2',
    borderRadius: '14px',
    p: 2,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar objectName={selectedDevice?.device_type.startsWith('Sensor') ? 'Edit sensor' : 'Edit controller'} />

      {/* Header */}
      <MDBox
        my={1}
        px={{ xs: 1, sm: 2 }}
        variant="gradient"
        style={{
          background: '#3244E2',
          height: '4rem',
          minHeight: '4rem',
          maxHeight: '4rem',
        }}
        borderRadius="lg"
        coloredShadow="dark">
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
          {canGoBack ? (
            <Button
              variant="text"
              onClick={() => navigate(-1)}
              sx={{
                color: '#fff',
                px: { xs: 0, sm: 1 },
                scale: { xs: 1.2, sm: 1.5 },
                fontWeight: 700,
                '&:hover': {
                  color: '#fff',
                  backgroundColor: 'transparent',
                  textShadow: '0 0 8px rgba(255, 255, 255, 0.85)',
                },
              }}>
              Back
            </Button>
          ) : (
            <Box />
          )}
          <Stack direction="row" spacing={2} alignItems="end">
            <MDTypography sx={{ fontSize: { xs: '1rem', sm: '1.5rem' }, fontWeight: 600 }} color="light">
              {selectedDevice ? (
                <>
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    Edit Device:{' '}
                  </Box>
                  {selectedDevice.name || selectedDevice.id}
                </>
              ) : (
                'Device Management'
              )}
            </MDTypography>
          </Stack>
          <Button
            variant="text"
            onClick={handleEditSubmit(onEditSubmit)}
            disabled={loading}
            sx={{
              color: '#fff',
              fontWeight: 700,
              px: { xs: 0, sm: 1 },
              scale: { xs: 1.2, sm: 1.5 },
              '&:hover': {
                color: '#fff',
                backgroundColor: 'transparent',
                textShadow: '0 0 8px rgba(255, 255, 255, 0.85)',
              },
            }}>
            Save
          </Button>
        </Stack>
      </MDBox>

      <Box
        sx={{
          margin: {
            xs: '0px 0px',
            md: '0px 10rem',
            lg: '0px 22rem',
            xl: '0px 30rem',
            xxl: '0px 42rem',
          },
        }}>
        <Grid container spacing={3}>
          {/* Device Not Found */}
          {!selectedDevice && deviceId && (
            <Grid item xs={12}>
              <Fade in={show} timeout={600} mountOnEnter>
                <Alert severity="error">
                  Device with ID {deviceId} not found or you don't have permission to access it.
                  <Button variant="text" onClick={() => navigate('/manage-sensors')} sx={{ ml: 2 }}>
                    ← Back to Devices List
                  </Button>
                </Alert>
              </Fade>
            </Grid>
          )}

          {/* DEVICE INFO SECTION */}
          {selectedDevice && (
            <Grid item xs={12} mt={2}>
              <Box sx={sectionCardSx}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <>Device name:</>{' '}
                      <strong>
                        {selectedDevice.name} ({selectedDevice.id})
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <>MAC Address:</> <strong>{getDisplayValue(selectedDevice.mac)}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <>Last Reading:</>{' '}
                      <strong>
                        {(() => {
                          const lastReadingTime = selectedDevice.last_reading_time;
                          return lastReadingTime ? formatDateTime(lastReadingTime) : 'N/A';
                        })()}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <>Created:</>{' '}
                      <strong>{selectedDevice.created_at ? formatDateTime(selectedDevice.created_at) : '-Unassigned-'}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <>Building:</> <strong>{getDisplayValue(selectedDevice.building)}</strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      <>Floor:</> <strong>{getDisplayValue(selectedDevice.floor_number)}</strong>
                    </Typography>
                  </Grid>
                  {selectedDevice.device_type.startsWith('Sensor') && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <>Area:</>{' '}
                        <strong>
                          {selectedDevice.is_full_floor === true ? 'Select' : getDisplayValue(selectedDevice.area_name)}
                        </strong>
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Grid>
          )}

          {/* EDIT DEVICE SECTION (General Info SECTION & Unit Configuration SECTION) */}
          {selectedDevice && (
            <Grid item xs={12}>
              <Box sx={{ backgroundColor: '#ffffff00' }}>
                <CardContent sx={{ p: 0 }}>
                  <form onSubmit={handleEditSubmit(onEditSubmit)}>
                    <Grid container spacing={3}>
                      <DeviceConfigurationSections
                        canManageUnitConfiguration={canManageUnitConfiguration}
                        controlEdit={controlEdit}
                        currentUser={currentUser}
                        errorsEdit={errorsEdit}
                        isControllerType={isControllerType}
                        isEmptyValue={isEmptyValue}
                        sectionCardSx={sectionCardSx}
                        sectionTitleSx={sectionTitleSx}
                        SENSOR_MODEL_OPTIONS={SENSOR_MODEL_OPTIONS}
                        CONTROLLER_MODEL_OPTIONS={CONTROLLER_MODEL_OPTIONS}
                        UNIT_TYPE_OPTIONS={UNIT_TYPE_OPTIONS}
                        UNIT_AIRFLOW_OPTIONS={UNIT_AIRFLOW_OPTIONS}
                        COOLING_TYPE_OPTIONS={COOLING_TYPE_OPTIONS}
                        HEATING_TYPE_OPTIONS={HEATING_TYPE_OPTIONS}
                        NOX_AVAILABLE_OPTIONS={NOX_AVAILABLE_OPTIONS}
                        RADAR_AVAILABLE_OPTIONS={RADAR_AVAILABLE_OPTIONS}
                        INACTIVE_DEVICE_NOTIFICATION_OPTIONS={INACTIVE_DEVICE_NOTIFICATION_OPTIONS}
                        watchEditDeviceIsController={watchEditDeviceIsController}
                        watchEditDeviceIsSensor={watchEditDeviceIsSensor}
                        watchEditUnitType={watchEditUnitType}
                      />

                      {false && (
                        <Grid item xs={12}>
                          <Typography sx={sectionTitleSx}>
                            <LocationOnOutlinedIcon sx={{ color: '#6a5ae0' }} />
                            Location & Placement
                          </Typography>
                          <Box sx={sectionCardSx}>
                            <Grid container spacing={2}>
                              {/* Building Dropdown */}
                              <Grid item xs={12} md={6}>
                                <Controller
                                  name="building_id"
                                  control={controlEdit}
                                  render={({ field: { onChange, value } }) => {
                                    const handleChange = e => {
                                      const nextBuildingId = e.target.value;
                                      const currentBuildingId = value || watchEditBuildingId || selectedDevice?.building_id;
                                      const loadedSourceSensorIds = Array.isArray(selectedDevice?.source_sensors_ids)
                                        ? selectedDevice.source_sensors_ids
                                        : [];
                                      const loadedCalculatedDecisionIds = Array.isArray(selectedDevice?.calculated_decision)
                                        ? selectedDevice.calculated_decision
                                        : [];
                                      const shouldWarnAboutSourceSensorReset =
                                        isControllerType &&
                                        // Collective mode can come from the saved config, legacy calculated_decision,
                                        // the current modal selection, or the local UI toggle state.
                                        (loadedSourceSensorIds.length > 0 ||
                                          loadedCalculatedDecisionIds.length > 0 ||
                                          selectedSensorIds.length > 0 ||
                                          useCalculatedSensing) &&
                                        !isEmptyValue(currentBuildingId) &&
                                        !isEmptyValue(nextBuildingId) &&
                                        String(currentBuildingId) !== String(nextBuildingId);

                                      // Changing the building clears collective source sensors.
                                      // Ask for confirmation before applying the selected building.
                                      if (shouldWarnAboutSourceSensorReset) {
                                        setSourceSensorsResetDialog({
                                          open: true,
                                          nextBuildingId,
                                        });
                                        return;
                                      }

                                      setValueEdit('floor_number', '');
                                      setValueEdit('floor_or_area_id', '');
                                      setValueEdit('area_id', '');
                                      onChange(nextBuildingId);

                                      if (String(currentBuildingId) !== String(nextBuildingId)) {
                                        refreshDevicesForBuilding(nextBuildingId);
                                      }
                                    };
                                    const options = buildingOptions.length
                                      ? buildingOptions
                                      : [{ name: 'No buildings available', value: '' }];
                                    return (
                                      <DropdownMenu
                                        options={options}
                                        selectedOption={value ?? ''}
                                        handleChange={handleChange}
                                        labelName="Building"
                                        emptyLabel="Select"
                                        disabled={buildingOptions.length === 0}
                                        error={!!errorsEdit.building_id}
                                        inline={true}
                                        labelWidth="15rem"
                                      />
                                    );
                                  }}
                                />
                              </Grid>
                              {/* Floor Dropdown */}
                              <Grid item xs={12} md={6}>
                                <Controller
                                  name="floor_number"
                                  control={controlEdit}
                                  render={({ field: { onChange, value } }) => {
                                    const options = !watchEditBuildingId
                                      ? [{ name: 'Select building first', value: '' }]
                                      : floorOptions.length > 0
                                        ? floorOptions
                                        : [{ name: 'No floors available', value: '' }];
                                    const handleChange = e => {
                                      const opt = options.find(o => String(o.value) === String(e.target.value));
                                      setValueEdit('floor_or_area_id', opt?.id || '');
                                      onChange(e.target.value);
                                    };
                                    const isDisabled = !watchEditBuildingId || floorOptions.length === 0;
                                    return (
                                      <DropdownMenu
                                        disabled={isDisabled}
                                        options={options}
                                        selectedOption={value || ''}
                                        handleChange={handleChange}
                                        labelName="Floor"
                                        error={!!errorsEdit.floor_number}
                                        inline={true}
                                        labelWidth="15rem"
                                      />
                                    );
                                  }}
                                />
                              </Grid>
                              {/* Area Dropdown */}
                              <Grid item xs={12} md={6}>
                                <Controller
                                  name="floor_or_area_id"
                                  control={controlEdit}
                                  render={({ field: { onChange, value } }) => {
                                    const hasFloor = !isEmptyValue(watchEditFloorNumber);
                                    const isDisabled = !hasFloor || areaOptions.length === 0;
                                    const options = !hasFloor
                                      ? [{ name: 'Select floor first', value: '' }]
                                      : areaOptions.length > 0
                                        ? areaOptions
                                        : [{ name: 'No areas available', value: '' }];
                                    const isAreaNotSelected = !options.some(o => Number(o.id) === Number(value));
                                    const selectedValue = isAreaNotSelected ? 'Select' : isEmptyValue(value) ? '' : String(value);
                                    return (
                                      <DropdownMenu
                                        disabled={isDisabled}
                                        options={options}
                                        selectedOption={selectedValue}
                                        handleChange={onChange}
                                        labelName="Area"
                                        error={!!errorsEdit.area_id}
                                        inline={true}
                                        labelWidth="15rem"
                                      />
                                    );
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Controller
                                  name="space"
                                  control={controlEdit}
                                  render={({ field: { onChange, value } }) => (
                                    <DropdownMenu
                                      options={SPACE_OPTIONS}
                                      selectedOption={value || ''}
                                      handleChange={onChange}
                                      labelName="Space"
                                      error={!!errorsEdit.space}
                                      inline={true}
                                      labelWidth="15rem"
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Unassign from Area Button */}
                              {/* <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                        <Button
                          variant="contained"
                          onClick={handleUnassignArea}
                          disabled={loading || !selectedDevice?.area_id}
                          sx={{
                              border: `2px solid ${brandColors.BLUE}`,
                              color: brandColors.BLUE,
                              backgroundColor: '#fff',
                              '&:hover': {
                                borderColor: brandColors.BLUE,
                                backgroundColor: brandColors.BLUE,
                                color: '#fff',
                              },
                              gap: '1rem',
                          }}>
                          Unassign from Area
                          <LocationOffIcon sx={{ scale: '1.5' }} />
                        </Button>
                        {!selectedDevice?.area_id && (
                          <Typography
                              variant="caption"
                              display="block"
                              sx={{ mt: 1, color: 'text.secondary' }}></Typography>
                        )}
                      </Grid> */}

                              <Grid
                                item
                                xs={12}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  marginTop: '-0.5rem',
                                  gap: '.75rem',
                                }}>
                                <Button
                                  className="one-click-btn"
                                  variant="inherit"
                                  onClick={() => setLocationModalOpen(true)}
                                  disabled={watchHideInFloorMap}
                                  sx={{
                                    ...FLOOR_MAP_ACTION_SX,
                                    gap: '.8rem',
                                    '&.Mui-disabled': {
                                      borderColor: '#d1d5db',
                                      color: '#9ca3af',
                                      backgroundColor: '#f9fafb',
                                    },
                                  }}>
                                  set the device floor location
                                  <EditLocationAltIcon sx={{ scale: '2' }} />
                                </Button>
                                <Controller
                                  name="hide_in_floor_map"
                                  control={controlEdit}
                                  render={({ field: { onChange, value } }) => (
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={!!value}
                                          onChange={event => {
                                            const checked = event.target.checked;
                                            onChange(checked);
                                            if (checked) {
                                              setValueEdit('map_x_value', -1, {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                              });
                                              setValueEdit('map_y_value', -1, {
                                                shouldDirty: true,
                                                shouldValidate: true,
                                              });
                                            }
                                          }}
                                          sx={{
                                            color: brandColors.BLUE,
                                            p: 0,
                                            mr: 1,
                                            '&.Mui-checked': { color: brandColors.BLUE },
                                          }}
                                        />
                                      }
                                      label={
                                        <Box
                                          component="span"
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '.6rem',
                                            fontWeight: 700,
                                            fontSize: '0.8rem',
                                            paddingX: '.3rem',
                                          }}>
                                          HIDE DEVICE IN FLOOR MAP
                                          <LocationOffIcon sx={{ scale: '2' }} />
                                        </Box>
                                      }
                                      sx={{
                                        ...FLOOR_MAP_ACTION_SX,
                                        m: 0,
                                        px: 1.5,
                                        py: 0,
                                        '&:hover .MuiCheckbox-root': {
                                          color: '#fff',
                                        },
                                        '&:hover .MuiCheckbox-root.Mui-checked': {
                                          color: '#fff',
                                        },
                                        '& .MuiFormControlLabel-label': {
                                          fontSize: '0.875rem',
                                          fontWeight: 500,
                                        },
                                      }}
                                    />
                                  )}
                                />
                                {/* Map X Coordinate */}
                                {/* <Grid item xs={6} md={2.5}>
                                  <Controller
                                    name="map_x_value"
                                    control={controlEdit}
                                    render={({ field: { onChange, value } }) => (
                                      <MDInput
                                        value={value || ''}
                                        label="Map X Coordinate"
                                        placeholder="X position on map"
                                        onChange={onChange}
                                        type="number"
                                        disabled={watchHideInFloorMap}
                                      />
                                    )}
                                  />
                                </Grid> */}
                                {/* Map Y Coordinate */}
                                {/* <Grid item xs={6} md={2.5}>
                                  <Controller
                                    name="map_y_value"
                                    control={controlEdit}
                                    render={({ field: { onChange, value } }) => (
                                      <MDInput
                                        value={value || ''}
                                        label="Map Y Coordinate"
                                        placeholder="Y position on map"
                                        onChange={onChange}
                                        type="number"
                                        disabled={watchHideInFloorMap}
                                      />
                                    )}
                                  />
                                </Grid> */}
                              </Grid>

                              {/* Collective Decision / Calculated Sensing Section */}
                              {isControllerType && (
                                <Grid
                                  item
                                  xs={12}
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    p: 0,
                                  }}>
                                  <Button
                                    className="one-click-btn"
                                    variant="inherit"
                                    color="primary"
                                    onClick={handleSelectDevicesClick}
                                    sx={{
                                      ...FLOOR_MAP_ACTION_SX,
                                      gap: '1rem',
                                    }}>
                                    Collective Decision Mode
                                    {/* <PlaylistAddIcon sx={{ scale: '2.4' }} /> */}
                                    <HubIcon sx={{ scale: '2' }} />
                                  </Button>
                                </Grid>
                              )}

                              {isControllerType && (
                                <DevicesSelect
                                  open={showDeviceSelectModal}
                                  onClose={handleCloseDeviceSelectModal}
                                  buildingSensors={buildingSensorsList}
                                  forcedBuildingId={deviceFormSelectedBuildingId}
                                  selectedDevices={selectedSensorIds}
                                  onSubmit={handleDeviceSelectSubmit}
                                  tooltipText="Aggregates occupancy levels from the selected sensors to calculate this controller’s occupancy level."
                                />
                              )}
                            </Grid>
                          </Box>
                        </Grid>
                      )}

                      <LocationPlacementSection
                        control={controlEdit}
                        setValue={setValueEdit}
                        errors={errorsEdit}
                        buildings={buildings}
                        users={users}
                        selectedDevice={selectedDevice}
                        resolvedAreaContext={resolvedAreaContext}
                        isControllerType={isControllerType}
                        isSensorType={!!watchEditDeviceIsSensor}
                        selectedSensorIds={selectedSensorIds}
                        setSelectedSensorIds={setSelectedSensorIds}
                        useCalculatedSensing={useCalculatedSensing}
                        setUseCalculatedSensing={setUseCalculatedSensing}
                        setSelectedFloorId={floorId => {
                          selectedFloorIdRef.current = floorId;
                        }}
                        sectionCardSx={sectionCardSx}
                        sectionTitleSx={sectionTitleSx}
                        isEmptyValue={isEmptyValue}
                      />

                      {/* Submit Buttons */}
                      <Grid item xs={12}>
                        <Stack
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexDirection: 'column',
                            gap: '.5rem',
                          }}>
                          <Button
                            variant="inherit"
                            onClick={handleReset}
                            sx={{
                              gap: '.5rem',
                              border: `1px solid ${brandColors.BLUE}`,
                              backgroundColor: '#fff',
                              color: brandColors.BLUE,
                              height: '3rem',
                              '&:hover': { backgroundColor: brandColors.BLUE, color: '#fff' },
                            }}>
                            Reset
                            <RestartAltIcon sx={{ scale: '2' }} />
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSaving}
                            sx={{
                              gap: '.5rem',
                              borderRadius: '12px',
                              minWidth: '260px',
                              height: '3rem',
                              backgroundColor: '#4b2bd6',
                              boxShadow: '0 6px 14px rgba(75, 43, 214, 0.28)',
                              '&:hover': { backgroundColor: '#4023bf' },
                            }}>
                            Save changes
                            <SaveIcon sx={{ scale: '1.5' }} />
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </form>
                </CardContent>
              </Box>
            </Grid>
          )}

          {/* DELETE DEVICE SECTION */}
          {selectedDevice && currentUser.role === 's' && (
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Delete Device"
                  sx={{
                    bgcolor: 'error.main',
                    '& .MuiCardHeader-title': {
                      color: '#ffffff',
                    },
                    '& .MuiCardHeader-subheader': {
                      color: '#ffffff',
                    },
                  }}
                />
                <CardContent>
                  <Typography variant="body1" gutterBottom>
                    Permanently delete this device from the system. This action cannot be undone.
                  </Typography>
                  <Button variant="contained" onClick={handleDeleteClick} disabled={isSaving} sx={{ mt: 2, background: 'red' }}>
                    Delete Device
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Back Button */}
          <Grid item xs={12}>
            <Button variant="text" onClick={() => navigate('/manage-sensors')} sx={{ mt: 2 }}>
              ← Back to Devices List
            </Button>
          </Grid>
        </Grid>
      </Box>

      {false && locationModalOpen && (
        <GenericModal
          open={locationModalOpen}
          onClose={handleLocationModalClose}
          title="Set device location"
          Component={DeviceLocation}
          componentProps={{
            floorSchemaKey,
            onPositionSelect: ({ x, y }) => {
              setValueEdit('hide_in_floor_map', false, {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValueEdit('map_x_value', Number(x.toFixed(2)), {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValueEdit('map_y_value', Number(y.toFixed(2)), {
                shouldDirty: true,
                shouldValidate: true,
              });
            },
          }}
          paperSx={{
            width: 'min(1560px, 99vw)',
            maxWidth: '99vw',
            height: '90vh',
            maxHeight: '99vh',
            display: 'flex',
            flexDirection: 'column',
          }}
          contentProps={{
            sx: {
              p: 1,
              flex: 1,
              minHeight: 0,
              display: 'flex',
              overflow: 'hidden',
            },
          }}
        />
      )}

      {false && (
        <AlertDialogSlide
          open={sourceSensorsResetDialog.open}
          title="Collective mode source sensors"
          content="This controller has collective mode enabled. Changing the building will reset the source sensors list."
          handleClose={handleSourceSensorsResetDialogClose}
          handleOk={handleSourceSensorsResetDialogOk}
        />
      )}

      {/* Delete Confirmation Modal */}
      <AlertDialogSlide
        open={deleteModalOpen}
        title="Confirm Device Deletion"
        content={`Are you sure you want to delete device "${selectedDevice?.name || selectedDevice?.id}"? This action cannot be undone.`}
        handleClose={handleDeleteModalClose}
        handleOk={handleDeleteConfirm}
      />

      {/* Notification Snackbar */}
      <CustomSnackbar
        open={notification.open}
        duration={6000}
        onClose={handleNotificationClose}
        vertical="top"
        horizontal="right"
        severity={notification.severity}
        message={notification.message}
      />
    </DashboardLayout>
  );
};

export default DeviceForm;
